import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import {
  FaUniversity, FaChartBar, FaUserGraduate, FaUsers, FaProjectDiagram,
  FaHandHoldingHeart, FaBriefcase, FaCalendarAlt, FaBars, FaCog, FaBell,
  FaEnvelope, FaTachometerAlt, FaUserTie, FaAward, FaBuilding, FaTimes,
  FaPlus, FaSchool, FaGraduationCap, FaMoneyBillWave, FaChartLine,FaFilePdf,FaFileExcel
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Dummy data for multiple colleges
const collegesData = [
  {
    id: 1,
    name: 'GEC Gandhinagar',
    location: 'Gandhinagar, Gujarat',
    students: 3000,
    alumni: 8000,
    faculty: 150,
    ranking: 3,
    fundingReceived: 6000000
  },
  {
    id: 2,
    name: 'GEC Modasa',
    location: 'Modasa, Gujarat',
    students: 2800,
    alumni: 7500,
    faculty: 140,
    ranking: 12,
    fundingReceived: 5500000
  },
  {
    id: 3,
    name: 'GEC Bhavnagar',
    location: 'Bhavnagar, Gujarat',
    students: 2600,
    alumni: 7000,
    faculty: 130,
    ranking: 15,
    fundingReceived: 5000000
  },
  {
    id: 4,
    name: 'GEC Surat',
    location: 'Surat, Gujarat',
    students: 3500,
    alumni: 9000,
    faculty: 160,
    ranking: 8,
    fundingReceived: 6500000
  },
  {
    id: 5,
    name: 'GEC Patan',
    location: 'Patan, Gujarat',
    students: 2400,
    alumni: 6800,
    faculty: 120,
    ranking: 18,
    fundingReceived: 4800000
  }
];

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [createCollegeOpen, setCreateCollegeOpen] = useState(false);
  const [createReportOpen, setCreateReportOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: FaTachometerAlt },
    { id: 'colleges', label: 'Colleges', icon: FaUniversity },
    { id: 'analytics', label: 'Analytics', icon: FaChartBar },
    { id: 'alumni', label: 'Alumni Network', icon: FaUserGraduate },
    { id: 'funding', label: 'Funding', icon: FaMoneyBillWave },
    { id: 'reports', label: 'Reports', icon: FaChartLine },
  ];

  const handleCreateCollege = (newCollege) => {
    console.log('New college created:', newCollege);
    setCreateCollegeOpen(false);
  };

  const handleCreateReport = (newReport) => {
    console.log('New report created:', newReport);
    setCreateReportOpen(false);
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return renderOverview();
      case 'colleges':
        return renderColleges();
      case 'analytics':
        return renderAnalytics();
      case 'alumni':
        return renderAlumniNetwork();
      case 'funding':
        return renderFunding();
      case 'reports':
        return renderReports();
      default:
        return renderOverview();
    }
  };

  const renderOverview = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">System Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Total Colleges</h3>
            <p className="text-3xl font-bold text-blue-600">{collegesData.length}</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Total Students</h3>
            <p className="text-3xl font-bold text-green-600">
              {collegesData.reduce((sum, college) => sum + college.students, 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-yellow-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Total Alumni</h3>
            <p className="text-3xl font-bold text-yellow-600">
              {collegesData.reduce((sum, college) => sum + college.alumni, 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-purple-100 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-2">Total Faculty</h3>
            <p className="text-3xl font-bold text-purple-600">
              {collegesData.reduce((sum, college) => sum + college.faculty, 0).toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <div className="col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">College Rankings</h3>
        <div className="h-80">
          <Bar
            data={{
              labels: collegesData.map(college => college.name),
              datasets: [{
                label: 'Ranking',
                data: collegesData.map(college => college.ranking),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  reverse: true,
                  title: {
                    display: true,
                    text: 'Ranking (lower is better)'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Funding Distribution</h3>
        <div className="h-80">
          <Doughnut
            data={{
              labels: collegesData.map(college => college.name),
              datasets: [{
                data: collegesData.map(college => college.fundingReceived),
                backgroundColor: [
                  'rgba(255, 99, 132, 0.8)',
                  'rgba(54, 162, 235, 0.8)',
                  'rgba(255, 206, 86, 0.8)',
                  'rgba(75, 192, 192, 0.8)',
                  'rgba(153, 102, 255, 0.8)',
                ],
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
            }}
          />
        </div>
      </div>

      <div className="col-span-1 md:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Activities</h3>
        <ul className="space-y-2">
          <li className="flex items-center justify-between bg-gray-100 p-2 rounded">
            <span><FaUniversity className="inline-block mr-2 text-blue-500" /> New college added: Gujarat Technical University</span>
            <span className="text-sm text-gray-500">2 days ago</span>
          </li>
          <li className="flex items-center justify-between bg-gray-100 p-2 rounded">
            <span><FaUserGraduate className="inline-block mr-2 text-green-500" /> Alumni network reached 50,000 members</span>
            <span className="text-sm text-gray-500">1 week ago</span>
          </li>
          <li className="flex items-center justify-between bg-gray-100 p-2 rounded">
            <span><FaMoneyBillWave className="inline-block mr-2 text-yellow-500" /> Annual funding report generated</span>
            <span className="text-sm text-gray-500">2 weeks ago</span>
          </li>
        </ul>
      </div>
    </div>
  );

  const renderColleges = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Colleges</h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setCreateCollegeOpen(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center shadow-md hover:bg-blue-600 transition duration-300"
          >
            <FaPlus className="mr-2" /> Add College
          </motion.button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 text-left">Name</th>
                <th className="py-2 px-4 text-left">Location</th>
                <th className="py-2 px-4 text-left">Students</th>
                <th className="py-2 px-4 text-left">Alumni</th>
                <th className="py-2 px-4 text-left">Faculty</th>
                <th className="py-2 px-4 text-left">Ranking</th>
              </tr>
            </thead>
            <tbody>
              {collegesData.map((college, index) => (
                <tr key={college.id} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                  <td className="py-2 px-4">{college.name}</td>
                  <td className="py-2 px-4">{college.location}</td>
                  <td className="py-2 px-4">{college.students.toLocaleString()}</td>
                  <td className="py-2 px-4">{college.alumni.toLocaleString()}</td>
                  <td className="py-2 px-4">{college.faculty}</td>
                  <td className="py-2 px-4">{college.ranking}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">College Size Distribution</h3>
        <div className="h-80">
          <Doughnut
            data={{
              labels: ['Large (5000+)', 'Medium (3000-5000)', 'Small (<3000)'],
              datasets: [{
                data: [
                  collegesData.filter(c => c.students >= 5000).length,
                  collegesData.filter(c => c.students >= 3000 && c.students < 5000).length,
                  collegesData.filter(c => c.students < 3000).length
                ],
                backgroundColor: [
                  'rgba(255, 99, 132, 0.8)',
                  'rgba(54, 162, 235, 0.8)',
                  'rgba(255, 206, 86, 0.8)',
                ],
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
            }}
          />
        </div>
      </div>

      <div className="col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Student-Faculty Ratio</h3>
        <div className="h-80">
          <Bar
            data={{
              labels: collegesData.map(college => college.name),
              datasets: [{
                label: 'Students per Faculty Member',
                data: collegesData.map(college => (college.students / college.faculty).toFixed(2)),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Ratio'
                  }
                }
              }
            }}
          />
        </div>
        </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">System Analytics</h2>
        <p className="text-gray-700 mb-4">Comprehensive analytics across all colleges in the system.</p>
      </div>

      <div className="col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Enrollment Trends</h3>
        <div className="h-80">
          <Line
            data={{
              labels: ['2018', '2019', '2020', '2021', '2022', '2023'],
              datasets: collegesData.map((college, index) => ({
                label: college.name,
                data: [
                  college.students * 0.8,
                  college.students * 0.85,
                  college.students * 0.9,
                  college.students * 0.95,
                  college.students * 0.98,
                  college.students
                ],
                borderColor: `hsl(${index * 137.5}, 70%, 50%)`,
                tension: 0.1,
                fill: false
              }))
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Students'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Alumni Growth Rate</h3>
        <div className="h-80">
          <Bar
            data={{
              labels: collegesData.map(college => college.name),
              datasets: [{
                label: 'Alumni Growth Rate (%)',
                data: collegesData.map(() => (Math.random() * 5 + 5).toFixed(2)),
                backgroundColor: 'rgba(153, 102, 255, 0.6)',
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Growth Rate (%)'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-100 p-4 rounded-lg">
            <h4 className="text-md font-semibold mb-2">Average Graduation Rate</h4>
            <p className="text-2xl font-bold text-blue-600">87.5%</p>
          </div>
          <div className="bg-green-100 p-4 rounded-lg">
            <h4 className="text-md font-semibold mb-2">Employment Rate</h4>
            <p className="text-2xl font-bold text-green-600">92.3%</p>
          </div>
          <div className="bg-yellow-100 p-4 rounded-lg">
            <h4 className="text-md font-semibold mb-2">Research Papers Published</h4>
            <p className="text-2xl font-bold text-yellow-600">1,234</p>
          </div>
          <div className="bg-purple-100 p-4 rounded-lg">
            <h4 className="text-md font-semibold mb-2">Avg. Alumni Donation</h4>
            <p className="text-2xl font-bold text-purple-600">$2,500</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAlumniNetwork = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Alumni Network</h2>
        <p className="text-gray-700 mb-4">Overview of the alumni network across all colleges.</p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Alumni Distribution by Industry</h3>
        <div className="h-80">
          <Doughnut
            data={{
              labels: ['Technology', 'Finance', 'Healthcare', 'Education', 'Manufacturing', 'Others'],
              datasets: [{
                data: [30, 20, 15, 12, 10, 13],
                backgroundColor: [
                  'rgba(255, 99, 132, 0.8)',
                  'rgba(54, 162, 235, 0.8)',
                  'rgba(255, 206, 86, 0.8)',
                  'rgba(75, 192, 192, 0.8)',
                  'rgba(153, 102, 255, 0.8)',
                  'rgba(255, 159, 64, 0.8)',
                ],
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
            }}
          />
        </div>
      </div>

      <div className="col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Alumni Engagement Rate</h3>
        <div className="h-80">
          <Bar
            data={{
              labels: collegesData.map(college => college.name),
              datasets: [{
                label: 'Engagement Rate (%)',
                data: collegesData.map(() => (Math.random() * 20 + 60).toFixed(2)),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100,
                  title: {
                    display: true,
                    text: 'Engagement Rate (%)'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="col-span-1 md:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Notable Alumni Achievements</h3>
        <ul className="space-y-2">
          <li className="flex items-center">
            <FaAward className="text-yellow-500 mr-2" />
            <span>15 Alumni recognized in Forbes 30 Under 30</span>
          </li>
          <li className="flex items-center">
            <FaAward className="text-yellow-500 mr-2" />
            <span>3 Alumni appointed as CEOs of Fortune 500 companies</span>
          </li>
          <li className="flex items-center">
            <FaAward className="text-yellow-500 mr-2" />
            <span>25 Alumni-founded startups received major funding</span>
          </li>
          <li className="flex items-center">
            <FaAward className="text-yellow-500 mr-2" />
            <span>2 Alumni awarded prestigious national science medals</span>
          </li>
        </ul>
      </div>
    </div>
  );

  const renderFunding = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-4">Funding Overview</h2>
        <p className="text-gray-700 mb-4">Comprehensive view of funding across all colleges in the system.</p>
      </div>

      <div className="col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Funding Received by College</h3>
        <div className="h-80">
          <Bar
            data={{
              labels: collegesData.map(college => college.name),
              datasets: [{
                label: 'Funding Received ($)',
                data: collegesData.map(college => college.fundingReceived),
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Funding Amount ($)'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Funding Sources</h3>
        <div className="h-80">
          <Doughnut
            data={{
              labels: ['Government Grants', 'Alumni Donations', 'Corporate Sponsorships', 'Research Grants', 'Other'],
              datasets: [{
                data: [40, 25, 15, 15, 5],
                backgroundColor: [
                  'rgba(255, 99, 132, 0.8)',
                  'rgba(54, 162, 235, 0.8)',
                  'rgba(255, 206, 86, 0.8)',
                  'rgba(75, 192, 192, 0.8)',
                  'rgba(153, 102, 255, 0.8)',
                ],
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
            }}
          />
        </div>
      </div>

      <div className="col-span-1 md:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Recent Major Funding Events</h3>
        <ul className="space-y-2">
          <li className="flex items-center justify-between bg-gray-100 p-2 rounded">
            <span><FaMoneyBillWave className="inline-block mr-2 text-green-500" /> $10M research grant awarded to LDRP Institute</span>
            <span className="text-sm text-gray-500">1 month ago</span>
          </li>
          <li className="flex items-center justify-between bg-gray-100 p-2 rounded">
            <span><FaMoneyBillWave className="inline-block mr-2 text-green-500" /> $5M alumni donation to KSV University</span>
            <span className="text-sm text-gray-500">2 months ago</span>
          </li>
          <li className="flex items-center justify-between bg-gray-100 p-2 rounded">
            <span><FaMoneyBillWave className="inline-block mr-2 text-green-500" /> $8M government grant for Sardar Patel University</span>
            <span className="text-sm text-gray-500">3 months ago</span>
          </li>
        </ul>
      </div>
    </div>
  );

  const renderReports = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Reports</h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setCreateReportOpen(true)}
            className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center shadow-md hover:bg-green-600 transition duration-300"
          >
            <FaPlus className="mr-2" /> Generate Report
          </motion.button>
        </div>
        <p className="text-gray-700 mb-4">Access and generate various reports for the university system.</p>
      </div>

      <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Available Reports</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 text-left">Report Name</th>
                <th className="py-2 px-4 text-left">Description</th>
                <th className="py-2 px-4 text-left">Last Generated</th>
                <th className="py-2 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="py-2 px-4">Annual Enrollment Report</td>
                <td className="py-2 px-4">Detailed breakdown of student enrollment across all colleges</td>
                <td className="py-2 px-4">2023-08-15</td>
                <td className="py-2 px-4">
                  <button className="text-blue-500 hover:text-blue-700 mr-2">View</button>
                  <button className="text-green-500 hover:text-green-700">Generate</button>
                </td>
              </tr>
              <tr className="bg-gray-50">
                <td className="py-2 px-4">Alumni Engagement Report</td>
                <td className="py-2 px-4">Analysis of alumni participation and contributions</td>
                <td className="py-2 px-4">2023-09-01</td>
                <td className="py-2 px-4">
                  <button className="text-blue-500hover:text-blue-700 mr-2">View</button>
                  <button className="text-green-500 hover:text-green-700">Generate</button>
                </td>
              </tr>
              <tr>
                <td className="py-2 px-4">Financial Summary Report</td>
                <td className="py-2 px-4">Overview of funding and financial status of all colleges</td>
                <td className="py-2 px-4">2023-07-30</td>
                <td className="py-2 px-4">
                  <button className="text-blue-500 hover:text-blue-700 mr-2">View</button>
                  <button className="text-green-500 hover:text-green-700">Generate</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="col-span-1 md:col-span-2 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Report Generation Frequency</h3>
        <div className="h-80">
          <Bar
            data={{
              labels: ['Annual', 'Bi-annual', 'Quarterly', 'Monthly'],
              datasets: [{
                label: 'Number of Reports',
                data: [5, 8, 12, 20],
                backgroundColor: 'rgba(75, 192, 192, 0.6)',
              }]
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Reports'
                  }
                }
              }
            }}
          />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Most Accessed Reports</h3>
        <ul className="space-y-2">
          <li className="flex items-center justify-between">
            <span>1. Annual Enrollment Report</span>
            <span className="text-blue-500">245 views</span>
          </li>
          <li className="flex items-center justify-between">
            <span>2. Financial Summary Report</span>
            <span className="text-blue-500">198 views</span>
          </li>
          <li className="flex items-center justify-between">
            <span>3. Alumni Engagement Report</span>
            <span className="text-blue-500">176 views</span>
          </li>
          <li className="flex items-center justify-between">
            <span>4. Faculty Performance Report</span>
            <span className="text-blue-500">152 views</span>
          </li>
        </ul>
      </div>
    </div>
  );

  const CreateCollegeForm = ({ onClose, onSubmit }) => {
    const [collegeData, setCollegeData] = useState({
      name: '',
      location: '',
      students: '',
      alumni: '',
      faculty: '',
      ranking: '',
      fundingReceived: ''
    });

    const handleChange = (e) => {
      const { name, value } = e.target;
      setCollegeData(prevData => ({ ...prevData, [name]: value }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      onSubmit(collegeData);
    };

    return (
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
      >
        <div className="p-6 flex-grow overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Add New College</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes size={24} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                College Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={collegeData.name}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter college name"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="location">
                Location
              </label>
              <input
                type="text"
                id="location"
                name="location"
                value={collegeData.location}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter college location"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="students">
                Number of Students
              </label>
              <input
                type="number"
                id="students"
                name="students"
                value={collegeData.students}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter number of students"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="alumni">
                Number of Alumni
              </label>
              <input
                type="number"
                id="alumni"
                name="alumni"
                value={collegeData.alumni}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter number of alumni"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="faculty">
                Number of Faculty
              </label>
              <input
                type="number"
                id="faculty"
                name="faculty"
                value={collegeData.faculty}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter number of faculty"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="ranking">
                Ranking
              </label>
              <input
                type="number"
                id="ranking"
                name="ranking"
                value={collegeData.ranking}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter college ranking"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="fundingReceived">
                Funding Received ($)
              </label>
              <input
                type="number"
                id="fundingReceived"
                name="fundingReceived"
                value={collegeData.fundingReceived}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter funding received"
              />
            </div>
          </form>
        </div>
        <div className="p-6 bg-gray-100 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
          >
            Add College
          </button>
        </div>
      </motion.div>
    );
  };

  const ReportTemplate = ({ report, onClose }) => {
    const [showTemplate, setShowTemplate] = useState(false);
  
    const handleGenerateReport = () => {
      // Simulate report generation
      setTimeout(() => {
        setShowTemplate(true);
      }, 1000);
    };
  
    const handleDownload = (type) => {
      // Simulate download
      console.log(`Downloading ${type} report`);
      // In a real application, this would trigger the actual download
    };
  
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
          className="bg-white rounded-lg p-6 w-full max-w-4xl"
        >
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">{report.name}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes size={24} />
            </button>
          </div>
  
          {!showTemplate ? (
            <div className="text-center">
              <p className="mb-4">Click the button below to generate the report.</p>
              <button
                onClick={handleGenerateReport}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
              >
                Generate Report
              </button>
            </div>
          ) : (
            <div>
              <div className="bg-gray-100 p-4 rounded-lg mb-4">
                <h3 className="text-xl font-semibold mb-2">{report.name}</h3>
                <p><strong>Type:</strong> {report.type}</p>
                <p><strong>Description:</strong> {report.description}</p>
                <p><strong>Generated on:</strong> {new Date().toLocaleString()}</p>
              </div>
  
              <div className="mb-4">
                <h4 className="text-lg font-semibold mb-2">Sample Data</h4>
                <table className="min-w-full bg-white border border-gray-300">
                  <thead>
                    <tr>
                      <th className="py-2 px-4 border-b">College</th>
                      <th className="py-2 px-4 border-b">Students</th>
                      <th className="py-2 px-4 border-b">Alumni</th>
                      <th className="py-2 px-4 border-b">Funding</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 px-4 border-b">College A</td>
                      <td className="py-2 px-4 border-b">5000</td>
                      <td className="py-2 px-4 border-b">15000</td>
                      <td className="py-2 px-4 border-b">$10M</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-4 border-b">College B</td>
                      <td className="py-2 px-4 border-b">4500</td>
                      <td className="py-2 px-4 border-b">12000</td>
                      <td className="py-2 px-4 border-b">$8M</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-4 border-b">College C</td>
                      <td className="py-2 px-4 border-b">3800</td>
                      <td className="py-2 px-4 border-b">10000</td>
                      <td className="py-2 px-4 border-b">$6M</td>
                    </tr>
                  </tbody>
                </table>
              </div>
  
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => handleDownload('PDF')}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition duration-300 flex items-center"
                >
                  <FaFilePdf className="mr-2" /> Download PDF
                </button>
                <button
                  onClick={() => handleDownload('Excel')}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-300 flex items-center"
                >
                  <FaFileExcel className="mr-2" /> Export to Excel
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  };
  const CreateReportForm = ({ onClose, onSubmit }) => {
    const [reportData, setReportData] = useState({
      name: '',
      type: '',
      description: '',
      frequency: '',
    });
    const [showTemplate, setShowTemplate] = useState(false);
  
    const handleChange = (e) => {
      const { name, value } = e.target;
      setReportData(prevData => ({ ...prevData, [name]: value }));
    };
  
    const handleSubmit = (e) => {
      e.preventDefault();
      setShowTemplate(true);
    };
  
    return (
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="fixed inset-y-0 right-0 w-full sm:w-96 bg-white shadow-lg flex flex-col"
      >
        {!showTemplate ? (
          <>
            <div className="p-6 flex-grow overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Generate New Report</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                  <FaTimes size={24} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                Report Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={reportData.name}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter report name"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="type">
                Report Type
              </label>
              <select
                id="type"
                name="type"
                value={reportData.type}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Report Type</option>
                <option value="Enrollment">Enrollment</option>
                <option value="Financial">Financial</option>
                <option value="Alumni">Alumni</option>
                <option value="Academic">Academic</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                value={reportData.description}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
                placeholder="Enter report description"
              ></textarea>
            </div>
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="frequency">
                Generation Frequency
              </label>
              <select
                id="frequency"
                name="frequency"
                value={reportData.frequency}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Frequency</option>
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
          </form>
          </div>
          <div className="p-6 bg-gray-100 flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition duration-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-300"
            >
              Generate Report
            </button>
          </div>
        </>
      ) : (
        <ReportTemplate
          report={reportData}
          onClose={() => {
            setShowTemplate(false);
            onClose();
          }}
        />
      )}
    </motion.div>
  );
};
  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <AnimatePresence>
        {(sidebarOpen || !isMobile) && (
          <motion.div
            initial={isMobile ? { x: "-100%" } : false}
            animate={{ x: 0 }}
            exit={isMobile ? { x: "-100%" } : false}
            transition={{ type: "tween" }}
            className={`bg-white text-gray-800 shadow-lg ${
              isMobile ? 'fixed inset-y-0 left-0 z-50 w-64' : 'w-64'
            }`}
          >
            <div className="p-6">
              <div className="mb-8 flex justify-between items-center">
                <h1 className="text-2xl font-bold">Admin Dashboard</h1>
                {isMobile && (
                  <button onClick={toggleSidebar} className="text-gray-600 hover:text-gray-800">
                    <FaTimes size={24} />
                  </button>
                )}
              </div>
              <nav>
                <ul className="space-y-2">
                  {sidebarItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => {
                          setActiveSection(item.id);
                          if (isMobile) toggleSidebar();
                        }}
                        className={`flex items-center w-full p-2 rounded ${
                          activeSection === item.id ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'
                        }`}
                      >
                        <item.icon className="mr-2" />
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="flex-1 overflow-y-auto p-8">
        <header className="bg-white shadow-md p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              {isMobile && (
                <button 
                  onClick={toggleSidebar} 
                  className="mr-4 bg-white p-2 rounded-full shadow-md text-gray-600 hover:text-gray-800 focus:outline-none"
                >
                  <FaBars size={24} />
                </button>
              )}
              <h1 className="text-3xl font-bold">
                {sidebarItems.find(item => item.id === activeSection)?.label}
              </h1>
            </div>
            <div className="flex space-x-4">
              <button className="text-gray-600 hover:text-gray-800"><FaBell size={24} /></button>
              <button className="text-gray-600 hover:text-gray-800"><FaEnvelope size={24} /></button>
              <button className="text-gray-600 hover:text-gray-800"><FaCog size={24} /></button>
            </div>
          </div>
        </header>
        {renderSection()}
      </div>

      <AnimatePresence>
        {createCollegeOpen && (
          <CreateCollegeForm
            onClose={() => setCreateCollegeOpen(false)}
            onSubmit={handleCreateCollege}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {createReportOpen && (
          <CreateReportForm
            onClose={() => setCreateReportOpen(false)}
            onSubmit={handleCreateReport}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;